/**
 * Print utility functions for generating printable views
 */

export interface PrintableStudent {
  id: string
  first_name: string
  last_name: string
  class_name?: string | null
  dob?: string
  date_of_birth?: string
  is_parishioner?: boolean
}

export interface PrintableClass {
  id: string
  name: string
  teachers?: string[]
  location?: string | null
  start_date?: string | null
}

export interface PrintClassStudentListOptions {
  parishName: string
  className: string
  teachers: string[]
  students: PrintableStudent[]
  printDate?: Date
}

export interface PrintStudentListOptions {
  parishName: string
  students: PrintableStudent[]
  classes: { id: string; name: string }[]
  printDate?: Date
}

/**
 * Print class student list
 * Includes: parish name, class name, teachers, date, and student list
 */
export function printClassStudentList(options: PrintClassStudentListOptions) {
  const { parishName, className, teachers, students, printDate = new Date() } = options

  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow pop-ups to print')
    return
  }

  const dateStr = printDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${className} - Student List</title>
        <style>
          @media print {
            @page {
              margin: 0.5in;
            }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
          }
          .parish-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .class-name {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .teachers {
            font-size: 14px;
            color: #333;
            margin-bottom: 5px;
          }
          .print-date {
            font-size: 12px;
            color: #666;
          }
          .student-list {
            margin-top: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th {
            background-color: #f0f0f0;
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }
          td {
            border: 1px solid #000;
            padding: 8px;
          }
          .no-students {
            text-align: center;
            padding: 20px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="parish-name">${escapeHtml(parishName)}</div>
          <div class="class-name">${escapeHtml(className)}</div>
          <div class="teachers">Teachers: ${teachers.map(t => escapeHtml(t)).join(', ') || 'None assigned'}</div>
          <div class="print-date">Printed: ${dateStr}</div>
        </div>
        <div class="student-list">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Date of Birth</th>
                <th>Parishioner</th>
              </tr>
            </thead>
            <tbody>
              ${students.length === 0
                ? '<tr><td colspan="3" class="no-students">No students assigned</td></tr>'
                : students.map(s => `
                  <tr>
                    <td>${escapeHtml(`${s.first_name} ${s.last_name}`)}</td>
                    <td>${escapeHtml(formatDate(s.dob || s.date_of_birth))}</td>
                    <td>${s.is_parishioner === false ? 'No' : 'Yes'}</td>
                  </tr>
                `).join('')}
            </tbody>
          </table>
        </div>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `)

  printWindow.document.close()
}

/**
 * Print student list (all students grouped by class)
 * Shows all classes and all student assignments
 */
export function printStudentList(options: PrintStudentListOptions) {
  const { parishName, students, classes: _classes, printDate = new Date() } = options

  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow pop-ups to print')
    return
  }

  const dateStr = printDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Helper function to calculate age
  function getAge(dob: string | null | undefined): number | null {
    if (!dob) return null
    try {
      const d = new Date(dob)
      const now = new Date()
      let age = now.getFullYear() - d.getFullYear()
      const m = now.getMonth() - d.getMonth()
      if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
      return age
    } catch {
      return null
    }
  }

  // Group students by class
  const studentsByClass = new Map<string, PrintableStudent[]>()
  const unassigned: PrintableStudent[] = []

  students.forEach(student => {
    if (student.class_name) {
      const className = student.class_name
      if (!studentsByClass.has(className)) {
        studentsByClass.set(className, [])
      }
      studentsByClass.get(className)!.push(student)
    } else {
      unassigned.push(student)
    }
  })

  // Sort students within each class alphabetically by last name
  studentsByClass.forEach((classStudents) => {
    classStudents.sort((a, b) => {
      const aLastName = (a.last_name || '').toLowerCase()
      const bLastName = (b.last_name || '').toLowerCase()
      return aLastName.localeCompare(bLastName)
    })
  })

  // Sort unassigned students by last name
  unassigned.sort((a, b) => {
    const aLastName = (a.last_name || '').toLowerCase()
    const bLastName = (b.last_name || '').toLowerCase()
    return aLastName.localeCompare(bLastName)
  })

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Student List - ${escapeHtml(parishName)}</title>
        <style>
          @media print {
            @page {
              margin: 0.5in;
            }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
          }
          .parish-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .print-date {
            font-size: 12px;
            color: #666;
          }
          .class-section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .class-header {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            padding: 8px;
            background-color: #f0f0f0;
            border: 1px solid #000;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th {
            background-color: #f0f0f0;
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }
          td {
            border: 1px solid #000;
            padding: 8px;
          }
          .no-students {
            text-align: center;
            padding: 20px;
            color: #666;
          }
          .unassigned {
            border-left: 4px solid #dc2626;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="parish-name">${escapeHtml(parishName)}</div>
          <div class="print-date">Student List - Printed: ${dateStr}</div>
        </div>

        ${Array.from(studentsByClass.entries()).map(([className, classStudents]) => `
          <div class="class-section">
            <div class="class-header">${escapeHtml(className)} (${classStudents.length} student${classStudents.length !== 1 ? 's' : ''})</div>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Date of Birth</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                ${classStudents.map(s => {
                  const age = getAge(s.dob || s.date_of_birth)
                  return `
                  <tr>
                    <td>${escapeHtml(`${s.first_name} ${s.last_name}`)}</td>
                    <td>${age !== null ? age : '—'}</td>
                    <td>${escapeHtml(formatDate(s.dob || s.date_of_birth))}</td>
                    <td>${escapeHtml(className)}</td>
                  </tr>
                `
                }).join('')}
              </tbody>
            </table>
          </div>
        `).join('')}

        ${unassigned.length > 0 ? `
          <div class="class-section unassigned">
            <div class="class-header">Unassigned Students (${unassigned.length})</div>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Date of Birth</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                ${unassigned.map(s => {
                  const age = getAge(s.dob || s.date_of_birth)
                  return `
                  <tr>
                    <td>${escapeHtml(`${s.first_name} ${s.last_name}`)}</td>
                    <td>${age !== null ? age : '—'}</td>
                    <td>${escapeHtml(formatDate(s.dob || s.date_of_birth))}</td>
                    <td>Unassigned</td>
                  </tr>
                `
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `)

  printWindow.document.close()
}

function escapeHtml(text: string | null | undefined): string {
  if (!text) return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

